/* ==========================================================================
   Tangent Quiz Helper Script
   --------------------------------------------------------------------------
   Drives the progress bar and (optionally) swaps the media in #video-container
   as the user advances through any Tangent quiz on the site.

   Two configuration patterns are supported:

   PATTERN 1 (preferred, new sections) — Data attributes on #video-container:
     - data-media-urls="url1|url2|url3|..."
         Pipe-delimited media URLs, one per question (1-based, in order).
         Empty entries keep the previous question's media in place.
     - data-media-mobile="show|hide-keep|hide-collapse|..."
         Optional. Per-slot mobile behavior at <=768px width:
           show           = show this media on mobile (default)
           hide-keep      = on mobile, keep showing the previous media instead
           hide-collapse  = on mobile, hide #video-container entirely
     - data-mobile-breakpoint="768"
         Optional override of the mobile breakpoint (px). Defaults to 768.

   PATTERN 2 (legacy, PillowID) — Hardcoded MEDIA_MAPS:
     Form-ID-keyed object below. Used only when #video-container has no
     data-media-urls attribute. Lets the existing PillowID section keep
     working without changes.

   Decision flow per quiz_progress event:
     1. If #video-container has data-media-urls → use Pattern 1
     2. Else if the active form ID is in MEDIA_MAPS → use Pattern 2
     3. Else → leave media untouched (static quizzes like Pump Club)
   ========================================================================== */

const VIDEO_EXTENSIONS = /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i;
const DEFAULT_MOBILE_BREAKPOINT = 768;

/* ----- Legacy Pattern 2: hardcoded media maps (PillowID) ----- */
const MEDIA_MAPS = {
  "67ea52f3a5f62bd5bac84f7d": {
    "67ea5343a5f62bd5bac85338":
      "https://cdn.shopify.com/videos/c/o/v/68d306d098144b73970b557c080e1e3a.mp4",
    "67ea5496a5f62bd5bac85acd":
      "https://cdn.shopify.com/videos/c/o/v/68d306d098144b73970b557c080e1e3a.mp4",
    "67ea52f3a5f62bd5bac84f87":
      "https://cdn.shopify.com/videos/c/o/v/166a4b41f648442494e2f494d0127c17.mp4",
    "67ea5317a5f62bd5bac85155":
      "https://cdn.shopify.com/videos/c/o/v/c44aefeae43840a28b5f33f63bfcc9ff.mp4",
    "67ea54aaa5f62bd5bac85ae3":
      "https://cdn.shopify.com/videos/c/o/v/d19d4197216442eb8f1fbf901532e63c.mp4",
    "67ea52f3a5f62bd5bac84f89":
      "https://cdn.shopify.com/videos/c/o/v/066964c0dc794d1f992ed33bac2252fc.mp4",
    "67ea5d50a5f62bd5bac886a8":
      "https://cdn.shopify.com/videos/c/o/v/6ded0d2e3c9346f989a894fe5d902d42.mp4",
    "67ea5d38a5f62bd5bac88687":
      "https://cdn.shopify.com/videos/c/o/v/5768fa193a844e83a57e4287a9c3e2a6.mp4",
  },
};

/* ----- Helpers ----- */

function isVideoUrl(url) {
  return VIDEO_EXTENSIONS.test(url);
}

function getActiveFormId() {
  const container = document.getElementById("tangent-main-container");
  return container ? container.getAttribute("data-formid") : null;
}

function getMobileBreakpoint() {
  const container = document.getElementById("video-container");
  if (!container) return DEFAULT_MOBILE_BREAKPOINT;
  const raw = container.getAttribute("data-mobile-breakpoint");
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MOBILE_BREAKPOINT;
}

function isMobile() {
  return window.matchMedia(`(max-width: ${getMobileBreakpoint()}px)`).matches;
}

/* ----- Pattern 1 lookup (data attributes) ----- */

function getDataAttrList(attrName) {
  const container = document.getElementById("video-container");
  if (!container) return [];
  const raw = container.getAttribute(attrName);
  if (!raw) return [];
  return raw.split("|").map((s) => s.trim());
}

function hasDataMediaUrls() {
  const container = document.getElementById("video-container");
  return !!(container && container.getAttribute("data-media-urls"));
}

function getPattern1Media(questionIndex) {
  const urls = getDataAttrList("data-media-urls");
  const modes = getDataAttrList("data-media-mobile");
  if (urls.length === 0) return { url: null, collapse: false };

  const idx = questionIndex - 1; // event payload is 1-based
  const url = urls[idx] || "";
  const mode = (modes[idx] || "show").toLowerCase();

  if (isMobile()) {
    if (mode === "hide-collapse") {
      return { url: null, collapse: true };
    }
    if (mode === "hide-keep") {
      return { url: null, collapse: false };
    }
  }

  return { url: url || null, collapse: false };
}

/* ----- Pattern 2 lookup (legacy MEDIA_MAPS) ----- */

function getPattern2Media(questionId) {
  const formId = getActiveFormId();
  if (!formId) return null;
  const map = MEDIA_MAPS[formId];
  if (!map) return null;
  return map[questionId] || null;
}

/* ----- Media element swap ----- */

function setContainerCollapsed(collapsed) {
  const container = document.getElementById("video-container");
  if (!container) return;
  container.style.display = collapsed ? "none" : "";
}

function updateMedia(questionIndex, questionId) {
  let url = null;
  let collapse = false;

  if (hasDataMediaUrls()) {
    const result = getPattern1Media(questionIndex);
    url = result.url;
    collapse = result.collapse;
  } else {
    url = getPattern2Media(questionId);
  }

  // Always reset collapsed state when we're NOT supposed to collapse,
  // so a previously-collapsed container reappears when we leave a hide-collapse slot.
  setContainerCollapsed(collapse);

  if (!url) return; // No mapping for this question — leave existing media untouched.

  const container = document.getElementById("video-container");
  if (!container) return;

  const currentEl = container.querySelector("video, img");
  const shouldBeVideo = isVideoUrl(url);
  const currentIsVideo = currentEl && currentEl.tagName === "VIDEO";

  // If media TYPE needs to change (img <-> video), replace the element.
  if (!currentEl || shouldBeVideo !== currentIsVideo) {
    const newEl = document.createElement(shouldBeVideo ? "video" : "img");
    if (shouldBeVideo) {
      newEl.autoplay = true;
      newEl.muted = true;
      newEl.loop = true;
      newEl.playsInline = true;
      newEl.setAttribute("playsinline", "");
    }
    newEl.src = url;
    newEl.style.width = "100%";
    if (currentEl) {
      currentEl.replaceWith(newEl);
    } else {
      container.appendChild(newEl);
    }
    if (shouldBeVideo && typeof newEl.load === "function") {
      newEl.load();
    }
    return;
  }

  // Same type — only update src if it changed (avoids restarting videos unnecessarily).
  if (currentEl.src !== url) {
    currentEl.src = url;
    if (currentIsVideo && typeof currentEl.load === "function") {
      currentEl.load();
    }
  }
}

/* ----- Progress bar ----- */

function updateProgressBar(present, total) {
  const progressBar = document.getElementById("progress-bar");
  if (!progressBar) return;
  if (present != 0) {
    progressBar.style.width = (present / total) * 100 + "%";
  }
}

function addQuestionBars(count) {
  const barsContainer = document.getElementById("bars-container");
  if (!barsContainer) return;
  for (let i = 0; i < count - 1; i++) {
    const bar = document.createElement("div");
    bar.classList.add("bars");
    bar.innerHTML = "&nbsp;";
    barsContainer.appendChild(bar);
  }
}

/* ----- Event listener ----- */

window.addEventListener("quiz_progress", (event) => {
  const progressData = event.detail.split("_");
  const presentQuestion = Number(progressData[0]); // 1-based question index
  const totalQuestion = Number(progressData[1]);
  const currentQuestionId = progressData[3];

  const progressBar = document.getElementById("progress-bar-block");
  const questionIndex = document.querySelector(".question-index-status");
  const floatingIndex = document.querySelector(".floating-index-status");

  if (questionIndex) {
    questionIndex.innerHTML = `${presentQuestion} out of ${totalQuestion}`;
  }
  if (floatingIndex) {
    floatingIndex.innerHTML = `${presentQuestion} out of ${totalQuestion}`;
  }

  updateMedia(presentQuestion, currentQuestionId);
  updateProgressBar(presentQuestion, totalQuestion);

  if (progressBar) {
    progressBar.style.display = presentQuestion != 0 ? "block" : "none";
  }

  const dotElements = document.getElementsByClassName("bars");
  if (dotElements.length === 0 && totalQuestion > 0) {
    addQuestionBars(totalQuestion);
  }
});

/* ----- Re-evaluate mobile state on viewport changes ----- */

window.addEventListener("resize", () => {
  // If the user rotates a tablet or resizes the window mid-quiz, re-apply
  // the mobile rules for whichever question we last rendered.
  const questionIndex = document.querySelector(".question-index-status");
  if (!questionIndex || !hasDataMediaUrls()) return;
  const match = questionIndex.innerHTML.match(/(\d+)\s+out of/);
  if (!match) return;
  const present = Number(match[1]);
  if (present > 0) {
    updateMedia(present, null);
  }
});